import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { MapPin, Search, Filter, Trash2, Map, ChevronDown, ChevronUp } from 'lucide-react';
import { trpc } from 'src/trpc';
import { useToast } from 'src/components/useToast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { StatusDisplay } from 'src/components/StatusDisplay';
import { NewCoverageAreaModal } from 'src/components/coverage-area/NewCoverageAreaModal';
import { EditCoverageAreaModal } from 'src/components/coverage-area/EditCoverageAreaModal';

interface CoverageArea {
  _id: string;
  zipCode: string;
  city?: string;
  state?: string;
  active?: boolean;
  organizationId?: string;
}

interface OrganizationCoverageTabProps {
  organizationId: string;
  onDataChange: (hasChanges: boolean) => void;
}

export const OrganizationCoverageTab: React.FC<OrganizationCoverageTabProps> = ({
  organizationId,
  onDataChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'suspended'>('all');
  const [selectedAreas, setSelectedAreas] = useState<Set<string>>(new Set());
  const [showExpanded, setShowExpanded] = useState(false);
  const [isMapViewOpen, setIsMapViewOpen] = useState(false);

  const { successToast, errorToast } = useToast();
  const utils = trpc.useUtils();

  // Fetch assigned coverage areas using existing endpoint
  const { data: coverageAreas = [], isLoading } =
    trpc.admin.organization.getOrganizationCoverageAreas.useQuery(organizationId || '');

  // Remove coverage area mutation using existing endpoint
  const { mutate: deleteCoverageArea } = trpc.admin.organization.removeCoverageArea.useMutation({
    onSuccess: (_, variables) => {
      utils.admin.organization.getOrganizationCoverageAreas.invalidate(organizationId);
      const area = coverageAreas.find(
        (a: CoverageArea) => a._id.toString() === variables.coverageAreaId,
      );
      successToast({
        title: 'Coverage Area Removed',
        description: `Coverage area ${area?.zipCode || ''} has been deactivated and unassigned.`,
      });
      onDataChange(true);
    },
    onError: (error) => {
      errorToast({
        title: 'Failed to Remove Coverage Area',
        description: error.message || 'An error occurred while removing the coverage area.',
      });
    },
  });

  // Filter coverage areas based on search and status
  const filteredAreas = coverageAreas.filter((area: CoverageArea) => {
    const matchesSearch =
      area.zipCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      area.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      area.state?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && area.active) ||
      (statusFilter === 'suspended' && !area.active);

    return matchesSearch && matchesStatus;
  });

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedAreas(new Set(filteredAreas.map((area) => area._id)));
    } else {
      setSelectedAreas(new Set());
    }
  };

  const handleToggleSelection = (areaId: string) => {
    const newSelection = new Set(selectedAreas);
    if (newSelection.has(areaId)) {
      newSelection.delete(areaId);
    } else {
      newSelection.add(areaId);
    }
    setSelectedAreas(newSelection);
  };

  const handleBulkDelete = () => {
    selectedAreas.forEach((areaId) => {
      deleteCoverageArea({ coverageAreaId: areaId });
    });
    setSelectedAreas(new Set());
  };

  const handleCoverageAreaSuccess = () => {
    onDataChange(true);
  };

  const activeCount = filteredAreas.filter((area) => area.active).length;

  // Show only first 5 areas when collapsed, all when expanded
  const displayedAreas = showExpanded ? filteredAreas : filteredAreas.slice(0, 4);
  const hasMoreAreas = filteredAreas.length > 4;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600">Loading coverage areas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Coverage Areas</CardTitle>
            <CardDescription>{filteredAreas.length} areas configured</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">{filteredAreas.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Active</CardTitle>
            <CardDescription>Currently accepting orders</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{activeCount}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Cities</CardTitle>
            <CardDescription>Unique cities covered</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-600">
              {new Set(filteredAreas.map((area) => area.city).filter(Boolean)).size}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Controls Row 1: Filters and Buttons */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
          <Select
            value={statusFilter}
            onValueChange={(value: 'all' | 'active' | 'suspended') => setStatusFilter(value)}
          >
            <SelectTrigger className="w-40">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
            </SelectContent>
          </Select>

          {hasMoreAreas && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowExpanded(!showExpanded)}
              className="flex items-center gap-2 text-muted-foreground w-40"
            >
              {showExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              {showExpanded ? 'Collapse' : `View All (${filteredAreas.length})`}
            </Button>
          )}
        </div>

        <div className="flex gap-2">
          <Dialog open={isMapViewOpen} onOpenChange={setIsMapViewOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Map className="h-4 w-4 mr-2" />
                View Map
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl">
              <DialogHeader>
                <DialogTitle>Coverage Areas Map</DialogTitle>
                <DialogDescription>
                  Visual representation of all coverage areas for this organization
                </DialogDescription>
              </DialogHeader>
              <div className="h-96 bg-gray-100 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <Map className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-600">Map integration would go here</p>
                  <p className="text-sm text-gray-500">Google Maps or similar mapping service</p>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <NewCoverageAreaModal
            organizationId={organizationId}
            onSuccess={handleCoverageAreaSuccess}
          />
        </div>
      </div>

      {/* Controls Row 2: Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search zip code, city, or state..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedAreas.size > 0 && (
        <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg">
          <span className="text-sm text-blue-700">
            {selectedAreas.size} area{selectedAreas.size > 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={handleBulkDelete} className="text-red-600">
              <Trash2 className="h-4 w-4 mr-1" />
              Remove Selected
            </Button>
          </div>
        </div>
      )}

      <Separator />

      {/* Coverage Areas List */}
      {filteredAreas.length === 0 ? (
        <div className="text-center py-12">
          <MapPin className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-500">No coverage areas found</h3>
          <p className="text-gray-400 mb-6">
            {searchTerm || statusFilter !== 'all'
              ? 'No areas match your search criteria'
              : 'This organization has no coverage areas assigned yet'}
          </p>
          <NewCoverageAreaModal
            organizationId={organizationId}
            onSuccess={handleCoverageAreaSuccess}
          />
        </div>
      ) : (
        <div className="space-y-4">
          {/* Data Table */}
          <div
            className={`border rounded-lg ${showExpanded ? 'max-h-[600px] overflow-y-auto' : ''}`}
          >
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <Checkbox
                      checked={
                        selectedAreas.size > 0 && selectedAreas.size === displayedAreas.length
                      }
                      onCheckedChange={handleSelectAll}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Zip Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    City
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    State
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayedAreas.map((area: CoverageArea) => (
                  <tr
                    key={area._id}
                    className={`${selectedAreas.has(area._id) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                  >
                    <td className="px-4 py-4">
                      <Checkbox
                        checked={selectedAreas.has(area._id)}
                        onCheckedChange={() => handleToggleSelection(area._id)}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{area.zipCode}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{area.city || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{area.state || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusDisplay status={area.active ? 'active' : 'suspended'} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex gap-2 justify-end">
                        <EditCoverageAreaModal
                          coverageAreaId={area._id}
                          onSuccess={handleCoverageAreaSuccess}
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteCoverageArea({ coverageAreaId: area._id })}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
